import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./keys.js";
import {
  postApiTasksFlowsCancel,
  postApiTasksFlowsList,
  postApiTasksRunsCancel,
  postApiTasksRunsList,
} from "@/lib/api/generated/task-flows/task-flows";

export function useConversationTaskFlowsQuery(tenantId: string, sessionKey?: string) {
  return useQuery({
    queryKey: queryKeys.conversationTaskFlows(tenantId, sessionKey ?? "none"),
    queryFn: async () =>
      await postApiTasksFlowsList({
        tenantId,
        sessionKey: sessionKey!,
      }),
    enabled: Boolean(tenantId && sessionKey),
    refetchOnWindowFocus: false,
  });
}

export function useConversationTaskRunsQuery(tenantId: string, sessionKey?: string) {
  return useQuery({
    queryKey: queryKeys.conversationTaskRuns(tenantId, sessionKey ?? "none"),
    queryFn: async () =>
      await postApiTasksRunsList({
        tenantId,
        sessionKey: sessionKey!,
      }),
    enabled: Boolean(tenantId && sessionKey),
    refetchOnWindowFocus: false,
  });
}

export function useCancelConversationTaskFlowMutation(tenantId: string, sessionKey?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (flowId: string) =>
      await postApiTasksFlowsCancel({ tenantId, sessionKey: sessionKey!, flowId }),
    onSuccess: async () => {
      if (!sessionKey) {
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversationTaskFlows(tenantId, sessionKey),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversationTaskRuns(tenantId, sessionKey),
        }),
      ]);
    },
  });
}

export function useCancelConversationTaskRunMutation(tenantId: string, sessionKey?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) =>
      await postApiTasksRunsCancel({ tenantId, sessionKey: sessionKey!, taskId }),
    onSuccess: async () => {
      if (!sessionKey) {
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversationTaskFlows(tenantId, sessionKey),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversationTaskRuns(tenantId, sessionKey),
        }),
      ]);
    },
  });
}
